import { Action, Module, Mutation } from "vuex-class-modules"
import range from "lodash/range"

import { cutFileName } from "@/helpers/cutFileName"
import { getUploadProgressByEachFile } from "@/helpers/onUploadProgress"
import { isTempAttachmentId, tempAttachmentId } from "@/helpers/tempAttachmentId"
import { apiService } from "@/services/api.service"
import { Endpoint } from "@/services/endpoints"
import { streamTokenService } from "@/services/stream-token.service"
import { projectModule } from "./project.module"
import { ResourceType } from "@/models/common"
import {
  AttachmentFileMetadataParams,
  DirectUploadPart,
  DirectUploadStart,
  DirectUploadTarget,
  FileDownloadSource,
  FileDownloadStatus,
  FileDownloadStatusesBySource,
  FileMetaData,
  UploadAttachment,
  UploadQueryFile
} from "@/models/File"
import { ServerResponse } from "@/models/ServerResponse"
import { store } from "../store"
import { Modules } from "../modules"
import { BaseModule } from "@/models/BaseModule"
import { HttpError } from "@/models/Error"
import { AlertContentItem, AlertType } from "@/models/Alert"
import { AlertActions, alertModule } from "./alert.module"
import { DEFAULT_MAX_UPLOAD_SIZE_MB } from "@/app.config"
import { debounceWithThrottle } from "@/utils/debounceWithThrottle"
import formatBytes from "@/helpers/formatBytes"
import { accountModule } from "./account.module"
import { userModule } from "./user.module"

export enum FileActions {
  ATTACHMENT_UPLOAD = "ATTACHMENT_UPLOAD",
  ATTACHMENT_UPLOAD_DIRECT = "ATTACHMENT_UPLOAD_DIRECT",
  ATTACHMENT_UPLOAD_TARGET = "ATTACHMENT_UPLOAD_TARGET",
  ATTACHMENT_UPLOAD_ITEM_DEMO_VIDEO = "ATTACHMENT_UPLOAD_ITEM_DEMO_VIDEO",
  ATTACHMENT_VIEW = "ATTACHMENT_VIEW",
  ATTACHMENT_DOCUMENT_PREVIEW = "ATTACHMENT_DOCUMENT_PREVIEW",
  ATTACHMENT_FILE_METADATA = "ATTACHMENT_FILE_METADATA",
  ATTACHMENT_METADATA = "ATTACHMENT_METADATA",
  ATTACHMENT_VIDEO_URL = "ATTACHMENT_VIDEO_URL",
  ATTACHMENT_DOWNLOAD = "ATTACHMENT_DOWNLOAD",
  ATTACHMENT_DOWNLOAD_FINISHED = "ATTACHMENT_DOWNLOAD_FINISHED",
  DELETE_FILE_FROM_TARGET = "DELETE_FILE_FROM_TARGET",
  DELETE_FILE = "DELETE_FILE",
  ADD_FILE_BY_ACCOUNT = "ADD_FILE_BY_ACCOUNT",
  DELETE_FILE_BY_ACCOUNT = "DELETE_FILE_BY_ACCOUNT",
  GET_MAX_UPLOAD_SIZE_KB = "GET_MAX_UPLOAD_SIZE_KB",
  GET_MAX_UPLOAD_SIZE_LABEL = "GET_MAX_UPLOAD_SIZE_LABEL",
  ABORT_ATTACHMENT_UPLOADS = "ABORT_ATTACHMENT_UPLOADS",
  SHOW_UPLOAD_SIZE_LIMIT_ALERT = "SHOW_UPLOAD_SIZE_LIMIT_ALERT",
  HAS_FILE_OVER_SIZE_LIMIT = "HAS_FILE_OVER_SIZE_LIMIT",
  GET_UPLOADING_FILES = "GET_UPLOADING_FILES",
  GET_UPLOAD_PROGRESS = "GET_UPLOAD_PROGRESS",
  GET_DOWNLOAD_STATUSES = "GET_DOWNLOAD_STATUSES",
  CLEAR_UPLOADED_FILES = "CLEAR_UPLOADED_FILES"
}

const DIRECT_UPLOAD_THRESHOLD_BYTES = 100 * 1024 * 1024
const UPLOAD_TOKEN_HEADER = "X-Upload-Token"
const PARALLEL_PARTS = 4
const UPLOAD_SIZE_LIMIT_ALERT_TIMEOUT = 20000
/** Clears the spinner when the server never reports the end, e.g. the stream request failed before sending the file. */
const DOWNLOAD_FINISHED_FALLBACK_TIMEOUT = 30 * 60 * 1000

const uploadAbortControllers = new Map<string, AbortController>()
const downloadFallbacks = new Map<string, ReturnType<typeof debounceWithThrottle>>()
let runningUploads = 0

function getUploadSignal(targetId: string) {
  let controller = uploadAbortControllers.get(targetId)
  if (!controller) {
    controller = new AbortController()
    uploadAbortControllers.set(targetId, controller)
  }
  return controller.signal
}

function onBeforeUnload(event: BeforeUnloadEvent) {
  event.preventDefault()
  event.returnValue = ""
  return ""
}

/** Asks the browser to confirm leaving the page while any attachment upload is running. */
function trackRunningUpload(running: boolean) {
  runningUploads += running ? 1 : -1
  if (running && runningUploads === 1) window.addEventListener("beforeunload", onBeforeUnload)
  if (!running && runningUploads === 0) window.removeEventListener("beforeunload", onBeforeUnload)
}

function toFormData(files: Blob[]) {
  const formData = new FormData()
  files.forEach(file => formData.append("file", file))
  return formData
}

type SendFormData = (
  files: Blob[],
  onUploadProgress?: (event: { loaded: number; total: number }) => void,
  signal?: AbortSignal
) => Promise<FileMetaData[] | HttpError>

@Module
class FileModule extends BaseModule {
  files: ServerResponse<FileMetaData>
  attachments: FileMetaData[] = []
  fileByAccount = false
  uploadingFiles: Record<string, FileMetaData[]> = {}
  uploadProgress: Record<string, number> = {}
  downloadStatuses: Record<string, FileDownloadStatusesBySource> = {}

  /** Files being uploaded to a Files tab, shown above its list until the list loads them from the server. */
  get [`get/${FileActions.GET_UPLOADING_FILES}`]() {
    return (uploadListKey: string) => this.uploadingFiles[uploadListKey] ?? []
  }

  get [`get/${FileActions.GET_UPLOAD_PROGRESS}`]() {
    return this.uploadProgress
  }

  get [`get/${FileActions.GET_DOWNLOAD_STATUSES}`]() {
    return this.downloadStatuses
  }

  /** Per-file upload limit of the current account's plan, as reported by the server. */
  get maxUploadSizeKb() {
    return (accountModule.account?.maxUploadSizeMb ?? DEFAULT_MAX_UPLOAD_SIZE_MB) * 1024
  }

  get [`get/${FileActions.GET_MAX_UPLOAD_SIZE_KB}`]() {
    return this.maxUploadSizeKb
  }

  get maxUploadSizeLabel() {
    return formatBytes(this.maxUploadSizeKb * 1024).toLowerCase()
  }

  get [`get/${FileActions.GET_MAX_UPLOAD_SIZE_LABEL}`]() {
    return this.maxUploadSizeLabel
  }

  get projectId() {
    return projectModule.currentProjectId
  }

  get accountId() {
    return accountModule.accountId
  }

  get [`get/${FileActions.ATTACHMENT_UPLOAD}`]() {
    return this.attachments.map(file => file.id)
  }

  @Mutation
  setAttachment(files: FileMetaData[]) {
    this.attachments = files
  }

  @Mutation
  toggleFileByAccount(fileByAccount: boolean) {
    this.fileByAccount = fileByAccount
  }

  @Mutation
  addUploadingFiles({ uploadListKey, files }: { uploadListKey: string; files: FileMetaData[] }) {
    this.uploadingFiles[uploadListKey] = [...files, ...(this.uploadingFiles[uploadListKey] ?? [])]
    files.forEach(file => (this.uploadProgress[file.id] = 0))
  }

  @Mutation
  setUploadProgress(progress: Record<string, number>) {
    Object.assign(this.uploadProgress, progress)
  }

  @Mutation
  setDownloadStatus({
    fileMetadataId,
    source,
    status
  }: {
    fileMetadataId: string
    source: FileDownloadSource
    status: FileDownloadStatus | null
  }) {
    const statuses = { ...this.downloadStatuses[fileMetadataId] }
    if (status) {
      statuses[source] = status
    } else {
      delete statuses[source]
    }

    if (Object.keys(statuses).length) {
      this.downloadStatuses[fileMetadataId] = statuses
      return
    }
    delete this.downloadStatuses[fileMetadataId]
  }

  @Mutation
  clearDownloadStatuses(fileMetadataId: string) {
    delete this.downloadStatuses[fileMetadataId]
  }

  @Mutation
  replaceUploadingFile({ uploadListKey, id, file }: { uploadListKey: string; id: string; file: FileMetaData }) {
    this.uploadingFiles[uploadListKey] = (this.uploadingFiles[uploadListKey] ?? []).map(item =>
      item.id === id ? file : item
    )
    delete this.uploadProgress[id]
  }

  @Mutation
  removeUploadingFiles({ uploadListKey, ids }: { uploadListKey: string; ids: string[] }) {
    this.uploadingFiles[uploadListKey] = (this.uploadingFiles[uploadListKey] ?? []).filter(
      file => !ids.includes(file.id)
    )
    ids.forEach(id => delete this.uploadProgress[id])
  }

  getValidAttachments(attachments: Blob[]) {
    const validAttachments = attachments.filter(attachment => {
      return attachment.size / 1024 < this.maxUploadSizeKb
    })
    if (validAttachments.length !== attachments.length) {
      const fileNames = attachments
        .filter(attachment => !validAttachments.includes(attachment))
        .map(attachment => (attachment as File).name)
        .filter(Boolean)
      this[FileActions.SHOW_UPLOAD_SIZE_LIMIT_ALERT]({ fileNames })
    }

    return validAttachments
  }

  /**
   * Shows the plan's per-file size limit, one toast per file over it with that file's name, for 20 seconds so
   * the name can be read; without file names (the server refused the size) a single toast is shown.
   */
  @Action
  [FileActions.SHOW_UPLOAD_SIZE_LIMIT_ALERT]({ fileNames = [] }: { fileNames?: string[] } = {}) {
    const limit: AlertContentItem = { text: this.maxUploadSizeLabel, type: "bold" }
    const contents: AlertContentItem[][] = fileNames.length
      ? fileNames.map(fileName => [
          { text: cutFileName(fileName), type: "bold" },
          { text: " — File size limit exceeded: ", type: "regular" },
          limit
        ])
      : [[{ text: "File size limit exceeded: ", type: "regular" }, limit]]

    contents.forEach(content =>
      alertModule[AlertActions.SHOW_ALERT]({
        type: AlertType.ERROR,
        theme: "toast",
        timeout: UPLOAD_SIZE_LIMIT_ALERT_TIMEOUT,
        queued: true,
        content
      })
    )
  }

  /** Shows the size-limit toast for each file over the plan's limit, so a form is never sent without that file. */
  @Action
  [FileActions.HAS_FILE_OVER_SIZE_LIMIT]({ files }: { files: { size: number; name: string }[] }) {
    const fileNames = files
      .filter(file => file.size / 1024 >= this.maxUploadSizeKb)
      .map(file => file.name)
    if (fileNames.length) this[FileActions.SHOW_UPLOAD_SIZE_LIMIT_ALERT]({ fileNames })
    return fileNames.length > 0
  }

  /**
   * With an upload list key, shows the files in that Files tab from the start of the upload: a placeholder with
   * its progress for each file, replaced by the stored file as soon as that file is done.
   */
  async uploadAttachments(
    attachments: Blob[],
    {
      onUploadProgress,
      uploadListKey,
      signal
    }: Pick<UploadAttachment, "onUploadProgress" | "uploadListKey"> & { signal?: AbortSignal },
    sendFormData: SendFormData,
    uploadTarget: DirectUploadTarget
  ): Promise<FileMetaData[] | HttpError> {
    const placeholders = uploadListKey ? this.addUploadPlaceholders(uploadListKey, attachments) : []
    const listedIds = placeholders.map(placeholder => placeholder.id)
    const onAllProgress = (event: { loaded: number; total: number }) => {
      if (placeholders.length) this.setUploadProgress(getUploadProgressByEachFile(event, placeholders))
      onUploadProgress?.(event)
    }
    const onFileUploaded = (attachment: Blob, uploaded: FileMetaData) => {
      const placeholder = placeholders[attachments.indexOf(attachment)]
      if (!uploadListKey || !placeholder) return
      this.replaceUploadingFile({
        uploadListKey,
        id: placeholder.id,
        file: FileMetaData.create(uploaded)
      })
      listedIds.push(uploaded.id)
    }

    trackRunningUpload(true)
    try {
      const data = await this.sendAttachments(
        attachments,
        onAllProgress,
        onFileUploaded,
        signal,
        sendFormData,
        uploadTarget
      )
      if (uploadListKey) {
        if (data instanceof HttpError) {
          this.removeUploadingFiles({ uploadListKey, ids: listedIds })
        } else {
          data.forEach((uploaded, index) => attachments[index] && onFileUploaded(attachments[index], uploaded))
          this.removeUploadingFiles({ uploadListKey, ids: placeholders.map(placeholder => placeholder.id) })
        }
      }
      return data
    } finally {
      trackRunningUpload(false)
    }
  }

  /** Shows a placeholder at 0% for every file of the upload, so queued files don't look uploaded. */
  addUploadPlaceholders(uploadListKey: string, attachments: Blob[]) {
    const placeholders = attachments.map(attachment => {
      const name = (attachment as File).name ?? "blob"
      return FileMetaData.create({
        id: tempAttachmentId(name),
        name,
        size: attachment.size,
        createdDate: new Date().toISOString(),
        targetType: name,
        owner: userModule.userProfile,
        custom: true
      })
    })
    this.addUploadingFiles({ uploadListKey, files: placeholders })
    return placeholders
  }

  /**
   * A file above the threshold goes part by part and the others one by one as before, in the order given, so
   * per-file progress keeps matching; the upload can be aborted and the browser asks before the page is left.
   */
  async sendAttachments(
    attachments: Blob[],
    onUploadProgress: (event: { loaded: number; total: number }) => void,
    onFileUploaded: (attachment: Blob, uploaded: FileMetaData) => void,
    signal: AbortSignal | undefined,
    sendFormData: SendFormData,
    uploadTarget: DirectUploadTarget
  ): Promise<FileMetaData[] | HttpError> {
    if (attachments.every(file => file.size <= DIRECT_UPLOAD_THRESHOLD_BYTES)) {
      return sendFormData(attachments, onUploadProgress, signal)
    }

    const total = attachments.reduce((size, file) => size + file.size, 0)
    const storedFiles: FileMetaData[] = []
    let uploadedBefore = 0

    for (const attachment of attachments) {
      const offset = uploadedBefore
      const onFileProgress = (event: { loaded: number }) =>
        onUploadProgress({ loaded: offset + Math.min(event.loaded, attachment.size), total })
      const data =
        attachment.size > DIRECT_UPLOAD_THRESHOLD_BYTES
          ? await this.uploadInParts(attachment, uploadTarget, onFileProgress, signal)
          : await sendFormData([attachment], onFileProgress, signal)

      if (data instanceof HttpError) return data
      storedFiles.push(...data)
      if (data[0]) onFileUploaded(attachment, data[0])
      uploadedBefore += attachment.size
    }

    return storedFiles
  }

  /**
   * Uploads one file part by part through the API, so no request lasts longer than one part and proxies don't
   * time out on large files. All checks (plan limit, size, access) are done by the backend.
   */
  async uploadInParts(
    file: Blob & { name?: string },
    uploadTarget: DirectUploadTarget,
    onUploadProgress?: (event: { loaded: number; total: number }) => void,
    signal?: AbortSignal
  ): Promise<FileMetaData[] | HttpError> {
    const accountId = this.accountId
    const start = await apiService.post<DirectUploadStart>(
      Endpoint.ATTACHMENT_DIRECT_UPLOAD_START(accountId),
      {
        projectId: uploadTarget.projectId ?? null,
        resourceType: uploadTarget.resourceType ?? null,
        targetId: uploadTarget.targetId ?? null,
        fileName: file.name ?? "blob",
        contentType: file.type || "application/octet-stream",
        size: file.size,
        demo: Boolean(uploadTarget.demo),
        excludeDuplicate: Boolean(uploadTarget.excludeDuplicate)
      },
      { signal }
    )
    if (start instanceof HttpError) return start

    const { uploadToken, partSize, partCount } = start
    const headers = { [UPLOAD_TOKEN_HEADER]: uploadToken }
    const parts: DirectUploadPart[] = []
    const loadedByPart: Record<number, number> = {}

    let nextPartNumber = 1
    /** Sends parts one after another; started PARALLEL_PARTS times, a freed slot takes the next part at once. */
    const sendNextPart = async (): Promise<HttpError | void> => {
      if (nextPartNumber > partCount) return
      const partNumber = nextPartNumber++
      const partStart = (partNumber - 1) * partSize
      const part = await apiService.post<DirectUploadPart>(
        Endpoint.ATTACHMENT_DIRECT_UPLOAD_PART(accountId, partNumber),
        file.slice(partStart, partStart + partSize),
        {
          headers: { ...headers, "Content-Type": "application/octet-stream" },
          signal,
          onUploadProgress: (event: ProgressEvent) => {
            loadedByPart[partNumber] = event.loaded
            const loaded = Object.values(loadedByPart).reduce((sum, part) => sum + part, 0)
            onUploadProgress?.({ loaded, total: file.size })
          }
        }
      )
      if (part instanceof HttpError) {
        nextPartNumber = partCount + 1
        return part
      }
      parts[partNumber - 1] = part
      return sendNextPart()
    }

    const results = await Promise.all(range(PARALLEL_PARTS).map(sendNextPart))
    const failedPart = results.find(result => result instanceof HttpError)
    if (failedPart instanceof HttpError) {
      this.cancelUploadInParts(accountId, headers)
      return failedPart
    }

    const files = await apiService.post<FileMetaData[]>(
      Endpoint.ATTACHMENT_DIRECT_UPLOAD_COMPLETE(accountId),
      parts,
      { headers, signal }
    )
    if (files instanceof HttpError) this.cancelUploadInParts(accountId, headers)

    return files
  }

  /** Lets the backend discard the parts of an upload that can't be finished. */
  cancelUploadInParts(accountId: string, headers: Record<string, string>) {
    void apiService.post(Endpoint.ATTACHMENT_DIRECT_UPLOAD_CANCEL(accountId), undefined, {
      headers
    })
  }

  @Action
  async [FileActions.ATTACHMENT_UPLOAD](params: UploadAttachment) {
    if (params?.targetId && params?.resourceType) {
      return this[FileActions.ATTACHMENT_UPLOAD_TARGET](params)
    }

    return this[FileActions.ATTACHMENT_UPLOAD_DIRECT](params)
  }

  @Action
  async [FileActions.ATTACHMENT_UPLOAD_DIRECT]({
    attachments,
    onUploadProgress,
    uploadListKey,
    onlySend,
    projectId,
    projectScoped
  }: UploadAttachment) {
    const validAttachments = this.getValidAttachments(attachments)
    if (!validAttachments.length) {
      return false
    }

    const selectedProjectId = projectId ? projectId : this.projectId
    const includeProjectId = projectScoped ?? !this.fileByAccount
    const endPoint = this.fileByAccount ? Endpoint.ATTACHMENT_UPLOAD_BY_ACCOUNT(this.accountId) : Endpoint.ATTACHMENT_UPLOAD(this.accountId)
    const data = await this.uploadAttachments(
      validAttachments,
      { onUploadProgress, uploadListKey },
      (files, onUploadProgress) =>
        apiService.post<FileMetaData[]>(endPoint, toFormData(files), {
          onUploadProgress,
          params: includeProjectId ? { projectId: selectedProjectId } : undefined
        }),
      { projectId: includeProjectId ? selectedProjectId : undefined }
    )
    return this.handleResponse<FileMetaData[]>(data, (data) => {
      const files = data.map(file => FileMetaData.create(file))
      if(!onlySend) {
        this.setAttachment(files)
      }

      return files
    }, true, () => {
      if (data instanceof HttpError && data.response?.status === 413) {
        this[FileActions.SHOW_UPLOAD_SIZE_LIMIT_ALERT]()
        return false
      }
    })
  }

  @Action
  async [FileActions.ATTACHMENT_UPLOAD_ITEM_DEMO_VIDEO](params: UploadAttachment) {
    const { attachments, targetId, onUploadProgress, projectId } = params
    if (!targetId) {
      return []
    }

    const validAttachments = this.getValidAttachments(attachments)
    if (!validAttachments.length) {
      return false
    }

    const selectedProjectId = projectId ? projectId : this.projectId
    const endPoint = Endpoint.ATTACHMENT_UPLOAD_ITEM_DEMO_VIDEO(
      this.accountId,
      selectedProjectId,
      "ITEM",
      targetId
    )

    const data = await this.uploadAttachments(
      validAttachments,
      { onUploadProgress, signal: getUploadSignal(targetId) },
      (files, onUploadProgress, signal) => {
        const queryFile: UploadQueryFile = {}

        if (onUploadProgress) {
          queryFile.onUploadProgress = onUploadProgress
        }
        if (signal) {
          queryFile.signal = signal
        }

        return apiService.post<FileMetaData[]>(endPoint, toFormData(files), queryFile)
      },
      {
        projectId: selectedProjectId,
        resourceType: "ITEM",
        targetId,
        demo: true
      }
    )

    return this.handleResponse<FileMetaData[]>(data, (data) => {
      const files = data.map(file => FileMetaData.create(file))

      return files
    }, true, () => {
      if (data instanceof HttpError && data.response?.status === 413) {
        this[FileActions.SHOW_UPLOAD_SIZE_LIMIT_ALERT]()
        return false
      }
    })
  }

  /** Supports both project-scoped and reusable account-scoped attachment targets. */
  @Action
  async [FileActions.ATTACHMENT_UPLOAD_TARGET](params: UploadAttachment) {
    const {
      attachments,
      resourceType,
      targetId,
      onUploadProgress,
      uploadListKey,
      excludeDuplicate,
      projectId,
      projectScoped
    } = params
    if (!resourceType || !targetId) {
      return []
    }

    const validAttachments = this.getValidAttachments(attachments)
    if (!validAttachments.length) {
      return false
    }

    const selectedProjectId = projectId ? projectId : this.projectId
    const includeProjectId = projectScoped ?? !this.fileByAccount
    const endPoint = this.fileByAccount ? Endpoint.ATTACHMENT_UPLOAD_TARGET_BY_ACCOUNT(this.accountId, resourceType, targetId) : Endpoint.ATTACHMENT_UPLOAD_TARGET(this.accountId, resourceType, targetId)

    const data = await this.uploadAttachments(
      validAttachments,
      { onUploadProgress, uploadListKey, signal: getUploadSignal(targetId) },
      (files, onUploadProgress, signal) => {
        const queryFile: UploadQueryFile = { params: {} }

        if (includeProjectId) {
          queryFile.params = { projectId: selectedProjectId }
        }

        if (excludeDuplicate !== undefined) {
          queryFile.params = {
            ...queryFile.params,
            excludeDuplicate: excludeDuplicate
          }
        }

        if (onUploadProgress) {
          queryFile.onUploadProgress = onUploadProgress
        }
        if (signal) {
          queryFile.signal = signal
        }

        return apiService.post<FileMetaData[]>(endPoint, toFormData(files), queryFile)
      },
      {
        projectId: includeProjectId ? selectedProjectId : undefined,
        resourceType,
        targetId,
        excludeDuplicate
      }
    )

    return this.handleResponse<FileMetaData[]>(data, (data) => {
      const files = data.map(file => FileMetaData.create(file))
      this.setAttachment(files)

      return files
    }, true, () => {
      if (data instanceof HttpError && data.response?.status === 413) {
        this[FileActions.SHOW_UPLOAD_SIZE_LIMIT_ALERT]()
        return false
      }
    })
  }

  @Action
  async [FileActions.ATTACHMENT_VIEW]({
    fileMetadataId,
    fullSize = false
  }: {
    fileMetadataId: string
    fullSize?: boolean
  }) {
    const endPoint = fullSize
      ? Endpoint.ATTACHMENT_DOWNLOAD_BY_ACCOUNT(this.accountId, fileMetadataId)
      : Endpoint.ATTACHMENT_BY_ACCOUNT(this.accountId, fileMetadataId)
    const data = await apiService.getBlob<string>(endPoint)
    return this.handleResponse<string>(data, async (data) => {
        return data
      },
      true
    )
  }

  /** Loads a server-converted PDF while keeping the original office attachment private. */
  @Action
  async [FileActions.ATTACHMENT_DOCUMENT_PREVIEW]({ fileMetadataId }: { fileMetadataId: string }) {
    const data = await apiService.getBlob<Blob>(
      Endpoint.ATTACHMENT_DOCUMENT_PREVIEW_BY_ACCOUNT(this.accountId, fileMetadataId)
    )
    return this.handleResponse<Blob>(data, data => data, true, undefined, true)
  }

  @Action
  async [FileActions.ATTACHMENT_METADATA]({ fileMetadataId }: { fileMetadataId: string }) {
    const data = await apiService.get<FileMetaData>(
      Endpoint.ATTACHMENT_METADATA(this.accountId, fileMetadataId)
    )
    return this.handleResponse<FileMetaData>(data, data => FileMetaData.create(data), true)
  }

  @Action
  async [FileActions.ATTACHMENT_FILE_METADATA]({
    resourceType,
    targetId,
    projectId,
    projectScoped = true,
    filter
  }: AttachmentFileMetadataParams) {
    const selectedProjectId = projectId ? projectId : this.projectId
    const query = filter.getJsonObj()
    if (projectScoped) query.append("projectId", selectedProjectId)
    const data = await apiService.get<ServerResponse<FileMetaData>>(
      Endpoint.ATTACHMENT_FILE_METADATA(this.accountId, resourceType, targetId),
      query
    )

    return this.handleResponse<ServerResponse<FileMetaData>>(
      data,
      data => {
        const content = data.content.map(file => FileMetaData.create(file))
        return new ServerResponse<FileMetaData>({
          ...data,
          content
        })
      },
      true
    )
  }

  @Action
  async [FileActions.DELETE_FILE_BY_ACCOUNT]({ fileMetadataId }: { fileMetadataId: string }) {
    const data = await apiService.delete<void>(
      Endpoint.DELETE_ATTACHMENT_BY_ACCOUNT(this.accountId, fileMetadataId)
    )
    return this.handleResponse<void>(data)
  }

  /**
   * Lets the browser stream the file to disk: attachments may be up to 5 GB, too large to hold as a Blob.
   * Repeat clicks are ignored only while the link is prepared, so a download the browser dropped can be retried.
   */
  @Action
  async [FileActions.ATTACHMENT_DOWNLOAD]({
    fileMetadataId,
    fileName,
    source
  }: {
    fileMetadataId: string
    fileName: string
    source?: FileDownloadSource
  }) {
    if (source && this.downloadStatuses[fileMetadataId]?.[source] === "preparing") return

    if (source) this.setDownloadStatus({ fileMetadataId, source, status: "preparing" })
    const data = await streamTokenService.getStreamUrl({
      accountId: this.accountId,
      projectId: "",
      fileMetadataId
    })

    return this.handleResponse<string>(data, (url) => {
      const link = document.createElement("a")
      link.href = `${url}&download=true`
      link.download = fileName
      link.click()

      if (!source) return
      this.setDownloadStatus({ fileMetadataId, source, status: "started" })
      if (!downloadFallbacks.has(fileMetadataId)) {
        downloadFallbacks.set(
          fileMetadataId,
          debounceWithThrottle(
            () => this[FileActions.ATTACHMENT_DOWNLOAD_FINISHED](fileMetadataId),
            DOWNLOAD_FINISHED_FALLBACK_TIMEOUT
          )
        )
      }
      downloadFallbacks.get(fileMetadataId)?.()
    }, false, () => {
      if (source) this.setDownloadStatus({ fileMetadataId, source, status: null })
    })
  }

  /** Called by the ATTACHMENT_DOWNLOAD_FINISHED server event once the file has been sent or the download aborted. */
  @Action
  [FileActions.ATTACHMENT_DOWNLOAD_FINISHED](fileMetadataId: string) {
    this.clearDownloadStatuses(fileMetadataId)
  }

  @Action
  async [FileActions.DELETE_FILE_FROM_TARGET]({ fileMetadataId, resourceType, targetId, projectId }: {
    fileMetadataId: string, resourceType: ResourceType,
    targetId: string, projectId?: string
  }) {
    const data = await apiService.delete<void>(
      Endpoint.DELETE_ATTACHMENT_FROM_TARGET(this.accountId, resourceType, targetId, fileMetadataId),
      undefined,
      { projectId: projectId ? projectId : this.projectId }
    )
    return this.handleResponse<void>(data)
  }

  @Action
  async [FileActions.DELETE_FILE]({ fileMetadataId } : { fileMetadataId: string, projectId?: string }) {
    const endPoint = this.fileByAccount
      ? Endpoint.DELETE_ATTACHMENT_BY_ACCOUNT(this.accountId, fileMetadataId)
      : Endpoint.DELETE_FILE(this.accountId, fileMetadataId)
    const data = await apiService.delete<string>(endPoint)
    return this.handleResponse<string>(data)
  }

  @Action
  [FileActions.ADD_FILE_BY_ACCOUNT](fileByAccount: boolean) {
    this.toggleFileByAccount(fileByAccount)
  }

  /**
   * Cancels the running uploads to the target when the user leaves it: requests are aborted and an unfinished
   * part-by-part upload is cancelled on the server. Stored files stay.
   */
  @Action
  [FileActions.ABORT_ATTACHMENT_UPLOADS]({ targetId }: { targetId: string }) {
    uploadAbortControllers.get(targetId)?.abort()
    uploadAbortControllers.delete(targetId)
  }

  /** Drops uploaded files from a Files tab's upload list once the tab has loaded them from the server. */
  @Action
  [FileActions.CLEAR_UPLOADED_FILES]({ uploadListKey, ids }: { uploadListKey: string; ids: string[] }) {
    this.removeUploadingFiles({ uploadListKey, ids })
  }



  @Action
  async [FileActions.ATTACHMENT_VIDEO_URL]({ projectId, attachmentId, projectScoped, force }: {
    projectId?: string, attachmentId: string, projectScoped?: boolean, force?: boolean
  }) {
    if (!attachmentId || isTempAttachmentId(attachmentId)) return ""

    const includeProjectId = projectScoped ?? !this.fileByAccount
    const selectedProjectId = includeProjectId ? projectId || this.projectId : ""

    // Account attachments use the same expiring file token as project attachments.
    const data = await streamTokenService.getStreamUrl({
      accountId: this.accountId,
      projectId: selectedProjectId,
      fileMetadataId: attachmentId
    }, { force })

    return this.handleResponse<string>(data, url => url, false, () => "", true)
  }
}

export const fileModule = new FileModule({ store, name: Modules.FILE })
